import { PERMISSIONS } from '@/utils/permissions'

export type PrintableDocType =
  | 'quotation'
  | 'contract'
  | 'invoice'
  | 'sales_order'
  | 'delivery_note'
  | 'credit_note'

export interface PrintableDocument {
  type: PrintableDocType
  /** List route the document lives under, e.g. ``/quotations``. */
  basePath: string
  /** Route name of the print page. */
  printRouteName: string
  /** Locale key for the document type label. */
  titleKey: string
  /** Prefix of the exported PDF file name. */
  filenamePrefix: string
  permission: string
  /** Fetch the rendered PDF; services load lazily so the router stays light. */
  fetchBlob: (id: string) => Promise<Blob>
}

export const PRINTABLE_DOCUMENTS: Record<PrintableDocType, PrintableDocument> = {
  quotation: {
    type: 'quotation',
    basePath: '/quotations',
    printRouteName: 'QuotationPrint',
    titleKey: 'common.printView.docTypes.quotation',
    filenamePrefix: 'quotation',
    permission: PERMISSIONS.QUOTATIONS,
    fetchBlob: async (id) => (await import('@/services/quotation.service')).default.generatePDF(id),
  },
  contract: {
    type: 'contract',
    basePath: '/contracts',
    printRouteName: 'ContractPrint',
    titleKey: 'common.printView.docTypes.contract',
    filenamePrefix: 'contract',
    permission: PERMISSIONS.CONTRACTS,
    fetchBlob: async (id) => (await import('@/services/contract.service')).default.generatePDF(id),
  },
  invoice: {
    type: 'invoice',
    basePath: '/invoices',
    printRouteName: 'InvoicePrint',
    titleKey: 'common.printView.docTypes.invoice',
    filenamePrefix: 'invoice',
    permission: PERMISSIONS.INVOICES_VIEW,
    fetchBlob: async (id) => (await import('@/services/invoice.service')).default.fetchPdfBlob(id),
  },
  sales_order: {
    type: 'sales_order',
    basePath: '/sales-orders',
    printRouteName: 'SalesOrderPrint',
    titleKey: 'common.printView.docTypes.salesOrder',
    filenamePrefix: 'sales-order',
    permission: PERMISSIONS.SALES_ORDERS,
    fetchBlob: async (id) => (await import('@/services/sales-order.service')).default.fetchPdfBlob(id),
  },
  delivery_note: {
    type: 'delivery_note',
    basePath: '/delivery-notes',
    printRouteName: 'DeliveryNotePrint',
    titleKey: 'common.printView.docTypes.deliveryNote',
    filenamePrefix: 'delivery-note',
    permission: PERMISSIONS.DELIVERY_NOTES_VIEW,
    fetchBlob: async (id) => (await import('@/services/delivery-note.service')).default.fetchPdfBlob(id),
  },
  credit_note: {
    type: 'credit_note',
    basePath: '/credit-notes',
    printRouteName: 'CreditNotePrint',
    titleKey: 'common.printView.docTypes.creditNote',
    filenamePrefix: 'credit-note',
    permission: PERMISSIONS.CREDIT_NOTES_VIEW,
    fetchBlob: async (id) => (await import('@/services/credit-note.service')).default.fetchPdfBlob(id),
  },
}

export function printFilename(doc: PrintableDocument, label: string): string {
  return `${doc.filenamePrefix}-${label}.pdf`
}
